import {
  useWSSnackbar,
  WSButton,
  WSContainer,
  WSElement,
  WSFlexBox,
  WSForm,
  WSGridCentered,
  WSIconList,
  WSPanel,
  WSSelect,
  WSText
} from "@wingspanhq/fe-component-library";
import queryString from "query-string";
import { ICollaboratorGroup } from "@wingspanhq/payments/dist/interfaces";
import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useBrowserPageTitle } from "../../../components/BrowserPageTitle/BrowserPageTitle";
import { ProjectOnboardingLayout } from "../../../components/ProjectOnboardingLayout/ProjectOnboardingLayout";
import {
  useCollaboratorGroupsQuery,
  useCollaboratorsQuery,
  useListCollaboratorSettingsAdditionalData
} from "../../../query/payments/queries";
import { WSQueries } from "../../../query/WSQuery";
import { useBulkUploadCollaboratorsImporter } from "./bulkUploadCollaboratorsImporter";
import { UploadCollaborator } from "./BulkUploadCollaboratorsStep1";

export interface BulkUploadCollaboratorsStep2Props {}

export const defaultCollaboratorGroupOption = {
  label: "Do not add collaborators to group",
  value: "NONE"
};

export const BulkUploadCollaboratorsStep2: React.FC<BulkUploadCollaboratorsStep2Props> = () => {
  useBrowserPageTitle("Wingspan - Bulk upload collaborators");
  const history = useHistory();
  const location = useLocation();

  const { openSnackbar } = useWSSnackbar();
  const [isFlatfileLoading, setIsFlatfileLoading] = useState(false);
  const [selectedCollaboratorGroup, setSelectedCollaboratorGroup] = useState(
    defaultCollaboratorGroupOption
  );

  const qCollaborators = useCollaboratorsQuery();
  const qCollaboratorGroups = useCollaboratorGroupsQuery();
  const bulkUploadCollaboratorsImporter = useBulkUploadCollaboratorsImporter(
    selectedCollaboratorGroup
  );

  const collaboratorSettingsAdditionalDataQuery = useListCollaboratorSettingsAdditionalData(
    {
      retry: false
    }
  );
  const additionalData = collaboratorSettingsAdditionalDataQuery.data || [];

  const queries = queryString.parse(location.search);

  const onUploadSpreadsheet = async () => {
    setIsFlatfileLoading(true);
    const results = await bulkUploadCollaboratorsImporter();
    const csvCollaborators = results.validData as Array<UploadCollaborator>;
    if (csvCollaborators.length === 0) {
      openSnackbar({
        duration: 5000,
        type: "warning",
        message:
          "Sorry! We could not find any collaborators in the file you uploaded"
      });
      return;
    }
    const nextUrl = queries.nextUrl as string;
    history.push({
      pathname: `/member/invoices/contacts/collaborators/bulk-create/review`,
      state: {
        results: csvCollaborators,
        selectedCollaboratorGroup,
        nextUrl
      }
    });
  };

  const requiredFields = ["First Name", "Last Name", "Email"];
  const optionalFields = ["Phone number", "External Collaborator ID"];

  additionalData.forEach(field => {
    if (field.required) {
      requiredFields.push(field.name);
    } else {
      optionalFields.push(field.name);
    }
  });

  return (
    <ProjectOnboardingLayout progress={(100 / 3) * 2}>
      <WSContainer verticalPadding>
        <WSGridCentered M={8} S={6} XS={6}>
          <WSText.Label color="grey600" mt="S" mb="M" pt="S">
            2 of 3
          </WSText.Label>
          <WSText.Heading2>
            Bulk upload collaborators with a spreadsheet
          </WSText.Heading2>
          <WSPanel mt="L">
            <WSQueries queries={{ qCollaborators, qCollaboratorGroups }}>
              {({ qCollaboratorGroups: { data: collaboratorGroups } }) => {
                const collaboratorGroupOptions = [
                  defaultCollaboratorGroupOption,
                  ...collaboratorGroups.map(collaboratorGroup => ({
                    label: collaboratorGroup.name,
                    value: (collaboratorGroup as any).collaboratorGroupId
                  }))
                ];

                const onSelectCollaboratorGroup = (value: any) => {
                  const collaboratorGroup = collaboratorGroupOptions.find(
                    cg => cg.value === value
                  );
                  setSelectedCollaboratorGroup(
                    collaboratorGroup ?? defaultCollaboratorGroupOption
                  );
                };

                return (
                  <>
                    <WSText.Paragraph>
                      Don't worry if your spreadsheet has other details, but
                      please make sure it contains the following columns.
                    </WSText.Paragraph>
                    <WSFlexBox mt="L" mb="M">
                      <WSElement style={{ flex: 1 }}>
                        <WSText.Heading4 mb="S">Required</WSText.Heading4>
                        <WSIconList.Checks items={requiredFields} mb="M" />
                      </WSElement>
                      <WSElement style={{ flex: 1 }}>
                        <WSText.Heading4 mb="S">Optional</WSText.Heading4>
                        <WSIconList.Checks items={optionalFields} mb="M" />
                      </WSElement>
                    </WSFlexBox>

                    {collaboratorGroups.length > 0 && (
                      <WSForm
                        defaultValues={{
                          collaboratorGroupId: selectedCollaboratorGroup.value
                        }}
                      >
                        <WSForm.Field
                          name="collaboratorGroupId"
                          label="Collaborator group"
                          component={WSSelect}
                          componentProps={{
                            options: collaboratorGroupOptions,
                            onChange: onSelectCollaboratorGroup
                          }}
                          my="M"
                        />
                      </WSForm>
                    )}

                    <WSButton
                      icon="upload"
                      fullWidth
                      onClick={onUploadSpreadsheet}
                      loading={isFlatfileLoading}
                    >
                      Upload spreadsheet
                    </WSButton>
                  </>
                );
              }}
            </WSQueries>
          </WSPanel>
        </WSGridCentered>
      </WSContainer>
    </ProjectOnboardingLayout>
  );
};
